import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { API } from "@/services/API";
import { validateTrailerType } from "@/utils/validation";
import { today } from "@/utils/date";
import { lockedBestOutbounds } from "@/utils/lockedData";

const initialState = {
  data: [],
  loading: false,
  error: null,
  // Both the market potential and load rates pages share this slice; only the
  // most recently dispatched request may write results, so a slow response
  // from a previous page cannot overwrite the current page's data.
  latestRequestId: null,
};

export const getBestOutbounds = createAsyncThunk(
  "search/get-best-outbounds",
  async (
    { trailerType, zip, pickupDate, useExtendedMarket = false, isAutoRefresh = false, pickupRadius },
    { rejectWithValue, getState },
  ) => {
    const isLocked = !(getState().user.data?.featuresUnlocked ?? false);

    if (isLocked) {
      return { data: lockedBestOutbounds, isAutoRefresh };
    }

    if (!trailerType || !zip || !validateTrailerType(trailerType) || isNaN(zip)) {
      return rejectWithValue("Invalid rate history parameters provided");
    }

    return API.post("/analytics/prediction", {
      departure: zip,
      forDate: pickupDate || today,
      trailerType,
      useOtherSources: true,
      useExtendedMarket,
      routeVsOutboundValues: [0.1, 0.3, 0.5, 0.7, 0.9],
      departureRadius: pickupRadius ? parseInt(pickupRadius) : null,
    })
      .then((response) => {
        return { data: response.bestOutboundDestinations, isAutoRefresh };
      })
      .catch((err) => {
        console.error("Error fetching best outbounds", err);
        return rejectWithValue(err);
      });
  },
);

const bestOutboundsSlice = createSlice({
  name: "bestOutbounds",
  initialState,
  reducers: {
    clearBestOutboundsState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBestOutbounds.pending, (state, action) => {
        const isAutoRefresh = action.meta.arg.isAutoRefresh;
        state.latestRequestId = action.meta.requestId;
        state.loading = !isAutoRefresh;
        state.error = null;
      })
      .addCase(getBestOutbounds.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestRequestId) return;

        const { data: newData } = action.payload;
        const hasDataChanged = JSON.stringify(state.data) !== JSON.stringify(newData);

        if (hasDataChanged) {
          state.data = newData;
          state.error = null;
        }

        state.loading = false;
      })
      .addCase(getBestOutbounds.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestRequestId) return;

        state.data = [];
        state.loading = false;
        state.error = "Error getting best outbounds";
      });
  },
});

export const { clearBestOutboundsState } = bestOutboundsSlice.actions;
export default bestOutboundsSlice.reducer;
